import { useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import type { ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

/**
 * Inline-edit state machine for a group's `customLabel`: draft text, begin/cancel/commit,
 * and the 320ms commit-dedupe needed because blur and Enter can both fire on completion.
 * Editing belongs to the group it began on, so moving to another group ends it.
 * Shared by any UI that lets a user append a label to a group's name.
 */
export const useEditableGroupLabel = ({
  commitCustomLabel,
  group,
  scanIsEditable,
}: {
  commitCustomLabel: (customLabel: string) => void;
  group: ScanGroupOptimized;
  scanIsEditable: boolean;
}) => {
  const [draftSuffix, setDraftSuffix] = useState('');
  const draftSuffixReference = useRef('');
  const lastCommitTimestampReference = useRef(0);
  const [editingGroupId, setEditingGroupId] = useState<string>();
  const editing = editingGroupId === group.id;
  const escapeDismissedReference = useRef(false);

  const beginEditing = () => {
    if (!scanIsEditable) return;
    lastCommitTimestampReference.current = 0;
    const initial = group.customLabel ?? '';
    draftSuffixReference.current = initial;
    setDraftSuffix(initial);
    setEditingGroupId(group.id);
  };

  const dismissWithoutSave = () => {
    const initial = group.customLabel ?? '';
    draftSuffixReference.current = initial;
    setDraftSuffix(initial);
    setEditingGroupId(undefined);
  };

  const commitSuffixFromField = () => {
    const now = Date.now();
    if (now - lastCommitTimestampReference.current < 320) {
      return;
    }
    lastCommitTimestampReference.current = now;
    commitCustomLabel(draftSuffixReference.current);
    setEditingGroupId(undefined);
  };

  const updateDraftSuffix = (value: string) => {
    draftSuffixReference.current = value;
    setDraftSuffix(value);
  };

  /** Commits the draft, unless a keydown Escape just dismissed it right before blur fired. */
  const commitUnlessJustDismissed = () => {
    if (escapeDismissedReference.current) {
      escapeDismissedReference.current = false;
      return;
    }
    commitSuffixFromField();
  };

  const cancelOrCommitOnKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      escapeDismissedReference.current = true;
      event.preventDefault();
      dismissWithoutSave();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
    }
  };

  return {
    beginEditing,
    cancelOrCommitOnKey,
    commitUnlessJustDismissed,
    draftSuffix,
    editing,
    updateDraftSuffix,
  };
};
